import { useCallback, useRef, useState } from 'react';
import { FunctionReturningPromise, PromiseType } from 'react-use/lib/misc/types';
import { AsyncState } from 'react-use/lib/useAsyncFn';
import { useMountedState } from 'react-use';
import { useEventCallback } from '@thimble/utility';

type StateFromFunctionReturningPromise<T extends FunctionReturningPromise> = AsyncState<
  PromiseType<ReturnType<T>>
>;

const useRequest = <T extends FunctionReturningPromise>(
  fn: T,
  options?: { initialState?: AsyncState<PromiseType<ReturnType<T>>> }
): [
  StateFromFunctionReturningPromise<T>,
  (...args: Parameters<T>) => ReturnType<T>,
  () => void
] => {
  const isMounted = useMountedState();

  const [state, setState] = useState<StateFromFunctionReturningPromise<T>>(
    options?.initialState || { loading: false }
  );

  const lock = useRef<ReturnType<T> | null>(null);

  const lastCallId = useRef(0);

  const nextArgs = useRef<[T, Parameters<T>] | null>(null);

  const iTrigger = useCallback(
    (fn: T, args: Parameters<T>): ReturnType<T> => {
      setState(prevState => ({ ...prevState, loading: true }));
      const id = ++lastCallId.current;
      return (lock.current = fn(...args)
        .then(value => {
          if (isMounted()) {
            if (id === lastCallId.current) {
              setState({ value, loading: false });
            }
          }
          return value;
        })
        .catch(error => {
          if (isMounted()) {
            if (id === lastCallId.current) {
              setState({ error, loading: false });
            }
          }
          throw error;
        })
        .finally(() => {
          lock.current = null;
        }) as ReturnType<T>);
    },
    [isMounted]
  );

  const mTrigger = useEventCallback((): void => {
    if (!lock.current && nextArgs.current) {
      const [fn, args] = nextArgs.current;
      nextArgs.current = null;
      iTrigger(fn, args).finally(mTrigger);
    }
  });

  const trigger = useEventCallback((...args: Parameters<T>): ReturnType<T> => {
    if (!lock.current) {
      return iTrigger(fn, args).finally(mTrigger) as ReturnType<T>;
    } else {
      nextArgs.current = [fn, args];
      return lock.current;
    }
  });

  const cancelAndClean = useEventCallback(() => {
    lastCallId.current++;
    setState(prevState => ({ ...prevState, value: undefined }));
  });

  return [state, trigger, cancelAndClean];
};

export default useRequest;
